import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Calculator, Zap, Download, PlayCircle, Save, TrendingUp, ExternalLink, Eye, EyeOff, RefreshCcw } from 'lucide-react';
import { formatApiError } from '../../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/* ================ SCORING RULES (mirror of site's Configuração de Pontos) ================ */
const ScoringRulesSection = () => {
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  const load = async () => {
    try { setLoading(true); const { data } = await axios.get(`${API}/fantasy/scoring-rules`); setRules(data); }
    catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  return (
    <section className="bg-slate-900/60 border border-white/10 rounded-2xl p-5" data-testid="scoring-rules-section">
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-2">
          <Calculator className="w-5 h-5 text-emerald-400" />
          <h2 className="text-lg font-bold text-white">Regras de Pontuação</h2>
        </div>
        <a href="/admin" className="text-xs text-emerald-300 hover:text-emerald-200 inline-flex items-center gap-1.5 px-2 py-1 rounded bg-emerald-500/10 border border-emerald-400/30" data-testid="edit-rules-link">
          <ExternalLink className="w-3 h-3" /> Editar no Site
        </a>
      </div>
      <div className="text-xs text-slate-400 mb-4 bg-amber-500/10 border border-amber-400/30 rounded-lg px-3 py-2 text-amber-100">
        As regras aqui espelham a <b>Configuração de Pontos</b> do site principal. Qualquer alteração (adicionar Passe Certo, mudar multiplicador, ocultar stat) reflete-se instantaneamente no Fantasy — <b>uma única fonte de verdade</b>. Regras marcadas como ocultas ({<EyeOff className="inline w-3 h-3" />}) não pontuam.
      </div>

      {loading ? <div className="text-slate-400 text-sm">A carregar...</div> : err ? (
        <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2">{err}</div>
      ) : (
        <div className="space-y-1.5">
          {rules.map((r) => (
            <div key={r.stat_key} className="flex items-center gap-3 bg-slate-800/70 rounded-lg px-3 py-2" data-testid={`rule-${r.stat_key}`}>
              <Eye className="w-3.5 h-3.5 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="text-sm text-white font-medium truncate">{r.label}</div>
                <div className="text-[10px] text-slate-500 font-mono truncate">{r.stat_key}</div>
              </div>
              <div className={`text-sm font-black tabular-nums ${r.multiplier >= 0 ? 'text-emerald-300' : 'text-red-300'}`}>
                {r.multiplier > 0 ? '+' : ''}{r.multiplier}
              </div>
              <div className="text-[10px] text-slate-500 uppercase tracking-wider">pts</div>
            </div>
          ))}
          {rules.length === 0 && <div className="text-slate-400 text-sm text-center py-4">Sem regras visíveis. Configura no site em <a href="/admin" className="text-emerald-300 underline">Configuração de Pontos</a>.</div>}
        </div>
      )}
    </section>
  );
};

/* ================ STATS PREVIEW PER MATCH DAY ================ */
const StatsEditorSection = () => {
  const [mds, setMds] = useState([]);
  const [mdId, setMdId] = useState('');
  const [rows, setRows] = useState([]);
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const [ok, setOk] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    axios.get(`${API}/fantasy/admin/match-days`).then((r) => {
      setMds(r.data);
      if (r.data.length && !mdId) setMdId(r.data[0].id);
    });
    // eslint-disable-next-line
  }, []);

  const load = async (id) => {
    if (!id) return;
    setLoading(true); setErr(''); setOk('');
    try {
      const { data } = await axios.get(`${API}/fantasy/admin/match-days/${id}/stats`);
      setRows(data.rows); setRules(data.rules);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setLoading(false); }
  };
  useEffect(() => { if (mdId) load(mdId); }, [mdId]);

  const importLive = async () => {
    setBusy(true); setErr(''); setOk('');
    try {
      const { data } = await axios.post(`${API}/fantasy/admin/match-days/${mdId}/import-live-game`);
      setOk(`Importados stats de ${data.imported} jogadores do Live Game.`);
      await load(mdId);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  const process = async () => {
    const isReproc = currentMd?.status === 'processed';
    const msg = isReproc
      ? 'Esta jornada já foi processada. Re-processar?\n\nVai re-importar os stats do Live Game (agora agrega TODAS as partidas do dia), recalcular pontos e corrigir os saldos. O cálculo é idempotente — pode-se correr quantas vezes for preciso.'
      : 'Finalizar esta jornada?\n\nVai importar os stats do Live Game, calcular a pontuação, pagar as apostas e abrir automaticamente a próxima jornada.';
    if (!window.confirm(msg)) return;
    setBusy(true); setErr(''); setOk('');
    try {
      const { data } = await axios.post(`${API}/fantasy/admin/match-days/${mdId}/process`);
      const imp = data.imported_from_live || 0;
      const nxt = data.next_match_day;
      const prefix = isReproc ? '🔄 Jornada re-processada' : '✅ Jornada finalizada';
      setOk(`${prefix} — ${data.processed} escalações${imp ? ` · ${imp} jogadores importados do Live Game` : ''}${nxt ? ` · Próxima jornada: ${nxt.date}` : ''}.`);
      const md = await axios.get(`${API}/fantasy/admin/match-days`);
      setMds(md.data);
      await load(mdId);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  const applyPrices = async () => {
    if (!window.confirm('Recalcular os preços do mercado com base nesta jornada?\n\nFórmula nova: quem jogou → novo = atual + (pontos-15)*0.15. Quem não jogou → perde 3% do preço (máx 1€M). Limite 4–20€M.')) return;
    setBusy(true); setErr(''); setOk('');
    try {
      const { data } = await axios.post(`${API}/fantasy/admin/match-days/${mdId}/apply-prices`);
      if (data.skipped > 0 && data.updated === 0) {
        setOk('Preços desta jornada já tinham sido aplicados. Nada a fazer.');
      } else {
        const top = data.changes.slice(0, 3).map((c) => `${c.player_name} ${c.delta >= 0 ? '+' : ''}${c.delta.toFixed(1)}€M`).join(' · ');
        setOk(`💰 ${data.updated} preços actualizados. Maiores subidas: ${top || '—'}`);
      }
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  const rebuildMarket = async () => {
    if (!window.confirm('⚠️ RESET COMPLETO DO MERCADO\n\nIsto vai:\n• Recalcular o OVR de todos (média das últimas 5 jornadas)\n• Resetar os preços para a nova base (4–15€M)\n• Replay de todas as jornadas processadas com a nova fórmula\n• Limpar e reconstruir o price_history\n\nContinuar?')) return;
    setBusy(true); setErr(''); setOk('');
    try {
      const { data } = await axios.post(`${API}/fantasy/admin/rebuild-market`);
      setOk(`🔄 Mercado reconstruído — ${data.profiles_reset} perfis resetados, ${data.jornadas_replayed} jornadas replayed.`);
      await load(mdId);
    } catch (ex) { setErr(formatApiError(ex)); } finally { setBusy(false); }
  };

  const currentMd = mds.find((m) => m.id === mdId);

  return (
    <section className="bg-slate-900/60 border border-white/10 rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-2">
        <Zap className="w-5 h-5 text-emerald-400" />
        <h2 className="text-lg font-bold text-white">Processamento da Jornada</h2>
      </div>
      <p className="text-xs text-slate-400 mb-4">
        Os stats <b>vêm automaticamente do Live Game do site</b>. Selecciona a jornada, lança os dados no Jogo Ao Vivo (pode ser domingo à noite, segunda, quando puderes) e clica <b>Finalizar Jornada</b> — a app calcula pontos, paga apostas, aplica preços e <b>abre automaticamente a próxima jornada</b>.
      </p>

      <div className="flex flex-wrap items-center gap-3 mb-4">
        <select value={mdId} onChange={(e) => setMdId(e.target.value)}
          className="bg-slate-800/70 border border-white/10 rounded-lg px-3 py-2 text-white text-sm"
          data-testid="stats-md-select">
          {mds.map((m) => <option key={m.id} value={m.id}>{`${m.label} · ${m.status}`}</option>)}
        </select>
        <button onClick={importLive} disabled={!mdId || busy}
          className="bg-slate-800 hover:bg-slate-700 text-white text-sm font-medium rounded-lg px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="import-live-btn"
          title="Pré-visualizar os stats importados sem processar">
          <Download className="w-4 h-4" /> Pré-visualizar Live Game
        </button>
        <button onClick={process} disabled={!mdId || busy}
          className="bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-sm font-black rounded-lg px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="process-md-btn">
          <PlayCircle className="w-4 h-4" /> {currentMd?.status === 'processed' ? 'Re-processar' : 'Finalizar Jornada'}
        </button>
        <button onClick={applyPrices} disabled={!mdId || busy || currentMd?.status !== 'processed'}
          className="bg-amber-500 hover:bg-amber-400 text-slate-900 text-sm font-black rounded-lg px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50"
          data-testid="apply-prices-btn"
          title="Recalcula os preços do mercado com base nesta jornada">
          <TrendingUp className="w-4 h-4" /> Aplicar Preços
        </button>
        <button onClick={rebuildMarket} disabled={busy}
          className="bg-red-500/90 hover:bg-red-500 text-white text-sm font-black rounded-lg px-3 py-2 inline-flex items-center gap-1.5 disabled:opacity-50 ml-auto"
          data-testid="rebuild-market-btn"
          title="Reset total: recalcula OVR, resetar preços e replay de todas as jornadas processadas">
          <RefreshCcw className="w-4 h-4" /> Reset Mercado
        </button>
      </div>

      {err && <div className="text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2 mb-3">{err}</div>}
      {ok && <div className="text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 rounded-lg px-3 py-2 mb-3">{ok}</div>}
      {currentMd?.status === 'processed' && (
        <div className="text-sm text-amber-300 bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2 mb-3">
          Esta jornada já foi processada. Resultados em <a href="/fantasy/ranking" className="underline">/fantasy/ranking</a>. Precisas corrigir? Clica <b>Re-processar</b> — o cálculo é idempotente e agrega agora todas as partidas do dia.
        </div>
      )}

      {loading ? <div className="text-slate-400 text-sm">A carregar...</div> : rows.length === 0 ? (
        <div className="text-slate-400 text-sm">Sem jogadores aprovados.</div>
      ) : (
        <>
          <p className="text-[11px] uppercase tracking-widest text-slate-500 mb-2">Pré-visualização dos stats (só leitura)</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-slate-400 uppercase border-b border-white/10">
                <tr>
                  <th className="px-2 py-2">Jogador</th>
                  {rules.map((r) => (
                    <th key={r.id} className="px-2 py-2 text-center whitespace-nowrap">
                      <div>{r.label}</div>
                      <div className="text-emerald-300/70 font-mono normal-case">×{r.multiplier}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((row) => (
                  <tr key={row.player_name} className="border-b border-white/5 hover:bg-white/5" data-testid={`stats-row-${row.player_name}`}>
                    <td className="px-2 py-2 text-white font-medium whitespace-nowrap">
                      {row.player_name}
                      <span className="text-[10px] ml-1 text-slate-400">{row.position_type}</span>
                    </td>
                    {rules.map((r) => (
                      <td key={r.id} className="px-2 py-2 text-center tabular-nums" data-testid={`stat-${row.player_name}-${r.stat_key}`}>
                        <span className={`${row.stats[r.stat_key] ? 'text-white font-bold' : 'text-slate-600'}`}>
                          {row.stats[r.stat_key] ?? '—'}
                        </span>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </section>
  );
};

/* ================ MAIN ================ */
const PontuacaoTab = () => (
  <div className="space-y-6">
    <ScoringRulesSection />
    <StatsEditorSection />
  </div>
);

export default PontuacaoTab;
